import React, {
    useCallback, useEffect, useState,
} from 'react';
import PropTypes from 'prop-types';
import { useAuth0 } from '@auth0/auth0-react';
import { toast } from 'react-toastify';
import Button from '../../Button';
import {
    ALIGN, COLORS,
} from '../../../../constants/theme';
import ModalDialog from '../Base/ModalDialog';
import customerDataRequest from '../../../../services/api/customer-data';
import Loader from '../../Loader';
import { LISTING_TYPE_ORGANIZATION, LISTING_TYPE_SPONSOR } from '../../../../constants/site';
import { getAuthToken } from '../../../../auth/authHelpers';

const ClaimListingDialog = ({
    open,
    handleCancel,
    handleClaim,
    title,
    content,
    listingType,
    listingId,
    ...otherProps
}) => {
    const {
        getAccessTokenSilently,
        isAuthenticated,
        user,
        error,
    } = useAuth0();

    const [isLoadingRequests, setIsLoadingRequests] = useState(false);
    const [existingRequest, setExistingRequest] = useState(false);
    const isOrganization = listingType === LISTING_TYPE_ORGANIZATION;

    const getExistingOwnershipRequests = useCallback(async () => {
        if (listingId && isAuthenticated && open) {
            const token = await getAuthToken(getAccessTokenSilently);

            setIsLoadingRequests(true);
            const baseUri = isOrganization ? '/organizations' : '/sponsors';
            await customerDataRequest('get', `${baseUri}/ownership-requests`, token)
                .then((response) => {
                    const ownershipKey = isOrganization
                        ? 'organization_salesforce_id' : 'sponsor_advertiser_id';

                    if (response.data.data.some((ownership) => ((ownership[ownershipKey] === listingId)
                        && !ownership?.reviewed_at))) {
                        setExistingRequest(true);
                    }

                    setIsLoadingRequests(false);
                })
                .catch(() => {
                    toast.error('There was an error attempting to retrieve request data. '
                        + 'Please try again in a few minutes');
                    setIsLoadingRequests(false);
                    handleCancel();
                });
        }
    }, [getAccessTokenSilently, isOrganization, listingId, isAuthenticated, open, handleCancel]);

    useEffect(() => {
        if (user && user.email_verified) {
            getExistingOwnershipRequests();
        }
    }, [getExistingOwnershipRequests, user]);

    const renderDialogContent = () => {
        let dialogContent = content;
        if (isLoadingRequests) {
            dialogContent = <Loader align={ALIGN.baseline} color={COLORS.secondary} />;
        }
        if (!isAuthenticated) {
            dialogContent = (
                <div>
                    If you do not have an account, you can sign up for
                    free. Click the “Log In” button at the top of the
                    page, then click “Sign up.”
                </div>
            );
        }
        if (existingRequest) {
            dialogContent = 'You have previously requested ownership of this page. '
                + 'An LPI representative is in the process of reviewing your request.';
        }

        if (user && !user.email_verified) {
            dialogContent = 'Before you can proceed with your request, '
                + 'please verify your email address for Parishes Online.';
        }

        return dialogContent;
    };

    /**
     * More verbose, but easier to read
     * @return {JSX.Element}
     */
    const renderDialogButtons = () => {
        if (!isAuthenticated || error) {
            return (
                <Button onClick={handleCancel} color={COLORS.flat}>
                    Cancel
                </Button>
            );
        }

        if (!user.email_verified) {
            return (
                <Button onClick={handleCancel} color={COLORS.flat}>
                    Ok
                </Button>
            );
        }

        if (!existingRequest) {
            return (
                <>
                    <Button onClick={handleCancel} color={COLORS.flat}>
                        Cancel
                    </Button>
                    <Button onClick={handleClaim} color={COLORS.flat} autoFocus>
                        Claim
                    </Button>
                </>
            );
        }

        return (
            <Button onClick={handleCancel} color={COLORS.flat}>
                Cancel
            </Button>
        );
    };

    const renderDialogTitle = useCallback(() => {
        let dialogTitle = title;
        if (isLoadingRequests) {
            dialogTitle = 'Retrieving Ownership Requests';
        }

        if (existingRequest) {
            dialogTitle = 'Existing request in review';
        }

        if (user && !user.email_verified) {
            dialogTitle = 'Please verify email';
        }

        return dialogTitle;
    }, [title, existingRequest, isLoadingRequests, user]);

    return (
        <ModalDialog
            title={renderDialogTitle()}
            content={(
                <>
                    {isLoadingRequests && <Loader align={ALIGN.baseline} color={COLORS.secondary} />}
                    {renderDialogContent()}
                </>
            )}
            open={open}
            onClose={handleCancel}
            buttons={(renderDialogButtons())}
            {...otherProps}
        />
    );
};

ClaimListingDialog.propTypes = {
    open: PropTypes.bool.isRequired,
    handleCancel: PropTypes.func.isRequired,
    handleClaim: PropTypes.func.isRequired,
    title: PropTypes.string.isRequired,
    content: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.node,
        PropTypes.arrayOf(PropTypes.node),
    ]),
    listingType: PropTypes.oneOf([LISTING_TYPE_ORGANIZATION, LISTING_TYPE_SPONSOR]).isRequired,
    listingId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
};

ClaimListingDialog.defaultProps = {
    content: null,
};

export default ClaimListingDialog;
